/**
 * Tour Steps Registry
 *
 * Maps URL pathnames to their StepType[] factories.
 * Uses matchPath from react-router-dom for dynamic segments.
 *
 * Rules:
 * - Both PT and EN aliases resolved to the same steps.
 * - Placeholder pages (bridges/2, bridges/3) return [].
 * - getTourStepsForRoute receives TourStepContext for per-role/feature filtering.
 */

import { matchPath } from 'react-router-dom';
import type { StepType } from '@reactour/tour';
import type { TourStepContext, TourStepFactory } from './types';

import { homeSteps } from './home';
import { projectsSteps } from './projects';
import { projectSteps, projectKanbanSteps, projectGanttSteps } from './project';
import { reportsSteps } from './reports';
import { portfolioAiSteps } from './portfolioAi';
import { intakeSteps } from './intake';
import { committeesSteps, intakeTriageSteps } from './committees';
import { bridge1Steps, bridge4Steps } from './bridges';
import {
  userApprovalsSteps,
  settingsSteps,
  alertsSteps,
  plansSteps,
  auditSteps,
} from './admin';
import {
  tenantsSteps,
  cadastrosResponsaveisSteps,
  cadastrosProgramasSteps,
  cadastrosOkrsSteps,
  okrDashboardSteps,
  profileSteps,
} from './superadmin';

/** Route entry: pattern (react-router syntax) + factory */
interface RouteEntry {
  pattern: string;
  factory: TourStepFactory;
}

const ROUTE_MAP: RouteEntry[] = [
  // Home
  { pattern: '/home', factory: homeSteps },

  // Projects list (PT + EN aliases)
  { pattern: '/projetos', factory: projectsSteps },
  { pattern: '/projects', factory: projectsSteps },

  // Project detail
  { pattern: '/projeto', factory: projectSteps },
  { pattern: '/projects/:projectId', factory: projectSteps },
  { pattern: '/projects/:projectId/kanban', factory: projectKanbanSteps },
  { pattern: '/projects/:projectId/gantt', factory: projectGanttSteps },

  // Reports
  { pattern: '/relatorios', factory: reportsSteps },
  { pattern: '/relatorios/portfolio-ia', factory: portfolioAiSteps },
  { pattern: '/okrs/dashboard', factory: okrDashboardSteps },

  // Intake / Bridges
  { pattern: '/app/intake', factory: intakeSteps },
  { pattern: '/app/committees', factory: committeesSteps },
  { pattern: '/app/committee/approvals', factory: intakeTriageSteps },
  { pattern: '/bridges/1/:projectId', factory: bridge1Steps },
  { pattern: '/bridges/1', factory: bridge1Steps },
  // bridges/2 and bridges/3 are placeholders — no tour
  { pattern: '/bridges/4', factory: bridge4Steps },

  // Admin
  { pattern: '/admin/aprovacoes', factory: userApprovalsSteps },
  { pattern: '/admin/settings/general', factory: settingsSteps },
  { pattern: '/admin/alerts', factory: alertsSteps },
  { pattern: '/admin/planos', factory: plansSteps },
  { pattern: '/admin/auditoria', factory: auditSteps },

  // SuperAdmin
  { pattern: '/superadmin/tenants', factory: tenantsSteps },

  // Cadastros
  { pattern: '/cadastros/responsaveis', factory: cadastrosResponsaveisSteps },
  { pattern: '/cadastros/programas', factory: cadastrosProgramasSteps },
  { pattern: '/cadastros/okrs', factory: cadastrosOkrsSteps },

  // Profile
  { pattern: '/perfil', factory: profileSteps },
];

/**
 * Returns the filtered StepType[] for the given pathname.
 * Returns [] when no tour exists for the route (hides the TourButton).
 *
 * Uses matchPath with `end: true` to avoid partial matches.
 * Falls back to matchPath with `end: false` for catch-all segments
 * like /projects/:projectId/kanban vs /projects/:projectId.
 */
export function getTourStepsForRoute(
  pathname: string,
  ctx: TourStepContext,
): StepType[] {
  // Try exact match first
  for (const entry of ROUTE_MAP) {
    const match = matchPath({ path: entry.pattern, end: true }, pathname);
    if (match) {
      return entry.factory(ctx);
    }
  }
  return [];
}
